import { useCallback, useEffect, useState } from "react";
import { UserPlus, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Broken images and links in articles with nobody's name on them. The weekly
 *  digest can't email a findings list to "nobody", so these quietly rot — this
 *  hands them to real people, a few at a time. */
export default function KbUnownedFindings({ onAssigned }) {
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [owner, setOwner] = useState("");
  const [picked, setPicked] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/kb/link-health/unowned")
      .then(({ data: d }) => { setData(d); setPicked([]); })
      .catch(() => setData(null));
  }, []);

  useEffect(() => {
    load();
    api.get("/kb/planner/assignees")
      .then(({ data: d }) => setPeople(d.assignees || []))
      .catch(() => setPeople([]));
  }, [load]);

  const assign = async (slugs) => {
    if (!owner) { toast.error("Pick who should own these"); return; }
    if (!slugs.length) { toast.error("Tick at least one article"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/kb/planner/assign-owner", { slugs, user_id: owner });
      toast.success(`${res.assigned} article${res.assigned === 1 ? "" : "s"} now owned by ${res.owner}`);
      load();
      onAssigned?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't assign that");
    } finally { setBusy(false); }
  };

  if (!data || !data.articles?.length) return null;
  const toggle = (slug) => setPicked((p) => (
    p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="kb-unowned-findings">
      <div className="flex flex-wrap items-center gap-3">
        <UserPlus className="h-4 w-4 text-amber-600" />
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100" data-testid="kb-unowned-headline">
          {data.findings} broken {data.findings === 1 ? "thing" : "things"} sit in {data.articles.length}
          {" "}article{data.articles.length === 1 ? "" : "s"} with no owner
        </p>
        <button type="button" onClick={() => setOpen((v) => !v)}
          data-testid="kb-unowned-toggle"
          className="flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900">
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {open ? "Hide the list" : "Hand them out"}
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        The weekly digest can only email a person, so anything unowned never reaches anyone.
      </p>

      {open && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select value={owner} onChange={(e) => setOwner(e.target.value)}
              data-testid="kb-unowned-owner"
              className="h-8 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 text-xs text-slate-700 dark:text-slate-200">
              <option value="">Choose an owner…</option>
              {people.map((p) => (
                <option key={p.user_id} value={p.user_id}>{p.name || p.email}</option>
              ))}
            </select>
            <Button size="sm" className="h-8 text-xs" disabled={busy || !owner || !picked.length}
              onClick={() => assign(picked)} data-testid="kb-unowned-assign-picked">
              {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Assign {picked.length || ""} selected
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy || !owner}
              onClick={() => assign(data.articles.map((a) => a.slug))}
              data-testid="kb-unowned-assign-all">
              Assign all {data.articles.length}
            </Button>
            <button type="button" data-testid="kb-unowned-select-all"
              onClick={() => setPicked(picked.length === data.articles.length
                ? [] : data.articles.map((a) => a.slug))}
              className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-primary">
              {picked.length === data.articles.length ? "Clear selection" : "Select every article"}
            </button>
          </div>

          <ul className="mt-3 max-h-72 space-y-1 overflow-y-auto" data-testid="kb-unowned-list">
            {data.articles.map((a) => (
              <li key={a.slug}>
                <label className="flex cursor-pointer items-center gap-2 rounded-md bg-white dark:bg-slate-900 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                  data-testid={`kb-unowned-row-${a.slug}`}>
                  <input type="checkbox" checked={picked.includes(a.slug)}
                    onChange={() => toggle(a.slug)} data-testid={`kb-unowned-check-${a.slug}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-slate-800 dark:text-slate-100">{a.name}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      {a.images ? `${a.images} image${a.images === 1 ? "" : "s"}` : ""}
                      {a.images && a.links ? " · " : ""}
                      {a.links ? `${a.links} link${a.links === 1 ? "" : "s"}` : ""}
                      {a.draft ? " · draft" : ""} · {a.category}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
